/**
 * "Respond" panel for an alert: 3 RTR quick-actions wired to the live
 * `/admin/rtr/commands` endpoint via `useCreateRtrCommand`.
 *
 * Quick actions:
 *  - Isolate device
 *  - Quarantine file (from alert.process path)
 *  - Kill process (from alert.pid when available)
 *
 * Below the buttons, the latest 5 commands for this device render with
 * their state pill — gives the operator instant feedback after firing.
 */
import { useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Ban, Shield, Skull, Terminal } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RtrConfirmDialog } from "./RtrConfirmDialog";
import { RtrStatePill } from "./RtrStatePill";
import { useCreateRtrCommand, useRtrCommands } from "@/hooks/security/useRtr";
import type { RtrKind } from "@/types/edr-rtr";

interface RespondPanelProps {
  alert: {
    id: string;
    deviceId: string;
    deviceName: string;
    process?: string;
    pid?: number;
    ruleName: string;
  };
}

type Action =
  | { kind: "isolate" }
  | { kind: "quarantine_file"; path: string }
  | { kind: "kill_pid"; pid: number };

export function RespondPanel({ alert }: RespondPanelProps) {
  const [pending, setPending] = useState<Action | null>(null);
  const create = useCreateRtrCommand();
  const recentQ = useRtrCommands({ deviceId: alert.deviceId, limit: 5 });
  const recent = (recentQ.data ?? []).slice(0, 5);

  const filePath = alert.process;
  const pid = alert.pid;

  const closeDialog = () => setPending(null);

  const runAction = async (reason: string) => {
    if (!pending) return;
    const payload =
      pending.kind === "isolate"
        ? {}
        : pending.kind === "quarantine_file"
        ? { path: pending.path }
        : { pid: pending.pid };
    await create.mutateAsync({
      deviceId: alert.deviceId,
      kind: pending.kind as RtrKind,
      payload: payload as never,
      reason,
      confirmAction: reason,
      ttlMinutes: 15,
    });
    closeDialog();
  };

  const buttons = useMemo(
    () => [
      {
        kind: "isolate" as const,
        label: "Isolate device",
        icon: Shield,
        tone: "destructive" as const,
        available: true,
        action: { kind: "isolate" } as Action,
      },
      {
        kind: "quarantine_file" as const,
        label: "Quarantine file",
        icon: Ban,
        tone: "default" as const,
        available: Boolean(filePath),
        action: filePath ? ({ kind: "quarantine_file", path: filePath } as Action) : null,
        hint: filePath,
      },
      {
        kind: "kill_pid" as const,
        label: "Kill process",
        icon: Skull,
        tone: "default" as const,
        available: typeof pid === "number" && pid > 0,
        action: typeof pid === "number" ? ({ kind: "kill_pid", pid } as Action) : null,
        hint: typeof pid === "number" ? `pid ${pid}` : undefined,
      },
    ],
    [filePath, pid],
  );

  const dialogTitle = pending
    ? pending.kind === "isolate"
      ? `Isolate ${alert.deviceName}?`
      : pending.kind === "quarantine_file"
      ? `Quarantine file on ${alert.deviceName}?`
      : `Kill PID ${pending.pid} on ${alert.deviceName}?`
    : "";

  const dialogDescription = pending
    ? pending.kind === "isolate"
      ? "The sensor will block all network traffic except its lifeline back to Smplify. Lift via 'Release isolation' in the RTR Console."
      : pending.kind === "quarantine_file"
      ? "Moves the file to the sensor's quarantine vault. The user will see a permission error on next open."
      : "Sends SIGKILL to the PID immediately if still running."
    : "";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {buttons.map((b) => (
          <Button
            key={b.kind}
            size="sm"
            variant={b.tone === "destructive" ? "destructive" : "outline"}
            disabled={!b.available || create.isPending}
            onClick={() => b.action && setPending(b.action)}
            title={b.available ? b.hint : "Not available from this alert"}
          >
            <b.icon className="h-3.5 w-3.5 mr-1.5" />
            {b.label}
          </Button>
        ))}
      </div>

      {!filePath && !pid && (
        <p className="text-[11px] text-muted-foreground">
          Quarantine and Kill require a file path / PID on the alert. Isolate works for any alert.
        </p>
      )}

      <div className="rounded-md border border-border bg-card/40">
        <div className="px-3 py-2 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            <Terminal className="h-3 w-3" /> Recent commands on this device
          </div>
          {recent.length > 0 && (
            <Badge variant="outline" className="text-[10px]">
              {recent.length}
            </Badge>
          )}
        </div>
        {recent.length === 0 ? (
          <div className="px-3 py-4 text-[11px] text-muted-foreground">
            No commands fired for this device yet.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {recent.map((c) => (
              <li key={c.id} className="px-3 py-2 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-mono text-foreground">{c.kind}</span>
                    <RtrStatePill state={c.state} />
                  </div>
                  {c.reason && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground truncate">
                      {c.reason}
                    </div>
                  )}
                </div>
                <span className="text-[10px] tabular-nums text-muted-foreground flex-none">
                  {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <RtrConfirmDialog
        open={pending !== null}
        onOpenChange={(o) => !o && closeDialog()}
        title={dialogTitle}
        description={dialogDescription}
        deviceName={alert.deviceName}
        badges={
          pending?.kind === "quarantine_file"
            ? [{ label: "Path", value: pending.path }]
            : pending?.kind === "kill_pid"
            ? [{ label: "PID", value: String(pending.pid) }]
            : []
        }
        destructive={pending?.kind === "isolate"}
        loading={create.isPending}
        onConfirm={runAction}
        confirmLabel={
          pending?.kind === "isolate"
            ? "Isolate"
            : pending?.kind === "quarantine_file"
            ? "Quarantine"
            : "Kill"
        }
      />
    </div>
  );
}
